import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLocalizedPath } from '@/hooks/useI18n';
import type { Author } from '@/data/author';

/**
 * Box de autoria (E-E-A-T) — exibido no rodapé das páginas de conteúdo.
 * Reforça experiência, expertise e confiança do autor para leitores e IAs.
 */
export function AuthorBox({ author }: { author: Author }) {
  const localize = useLocalizedPath();
  const [imgError, setImgError] = useState(false);

  return (
    <section className="mt-12 pt-8 border-t border-sand-200 max-w-3xl" aria-label="Sobre o autor">
      <span className="font-body text-xs text-coral font-semibold uppercase tracking-wider">
        Quem escreve este guia
      </span>
      <div className="mt-3 bg-white rounded-2xl border border-sand-200 p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
        <div className="flex flex-col sm:flex-row gap-5">
          {/* Foto ou iniciais */}
          <div className="flex-shrink-0">
            {author.photo && !imgError ? (
              <img
                src={author.photo}
                alt={author.name}
                onError={() => setImgError(true)}
                className="w-20 h-20 rounded-full object-cover border-2 border-sand-200"
                loading="lazy"
                decoding="async"
              />
            ) : (
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-teal-deep to-teal-light flex items-center justify-center text-white font-display text-2xl select-none">
                {author.initials}
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="font-display text-xl text-warm-dark tracking-wide">{author.name}</div>
            <p className="font-body text-sm text-coral font-semibold">{author.role}</p>

            {author.experienceLine && (
              <p className="font-body text-xs text-warm-gray mt-1 flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {author.experienceLine}
              </p>
            )}

            <p className="font-body text-sm text-warm-gray leading-relaxed mt-3">{author.bio}</p>

            {author.expertise?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4">
                {author.expertise.map((e) => (
                  <span
                    key={e}
                    className="inline-flex items-center bg-teal-light/10 text-teal-deep border border-teal-light/20 font-body text-xs font-semibold px-2.5 py-1 rounded-full"
                  >
                    {e}
                  </span>
                ))}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-4 pt-4 border-t border-sand-100">
              <span className="inline-flex items-center gap-1.5 font-body text-xs text-emerald-700 font-medium">
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Conteúdo baseado em vivência local
              </span>
              {author.social.instagram && (
                <a
                  href={`https://instagram.com/${author.social.instagram}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-body text-xs text-teal-deep hover:underline"
                >
                  @{author.social.instagram}
                </a>
              )}
              {author.profileUrl && (
                <Link
                  to={localize(author.profileUrl)}
                  className="font-body text-xs font-semibold text-teal-deep hover:underline sm:ml-auto"
                >
                  Todos os textos →
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
